// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Contact materials. Shapes do not own their response coefficients — materials do.
//
// The values are calibration seeds drawn from Visual Pinball community practice and
// nFozzy's measurements of real machines. The authority is the 27 acceptance behaviours
// in 02 §9, not this table; calibration adjusts these until those pass.
//
// See 02-physics-and-feel-specification.md §7.

import { toRadians } from '../math/deterministic'

export type MaterialName =
  | 'rubber'
  | 'flipperRubber'
  | 'metal'
  | 'wood'
  | 'plastic'
  | 'wire'
  | 'glass'

export interface Material {
  readonly name: MaterialName
  /**
   * Position in `MATERIAL_NAMES`.
   *
   * A material has to be nameable across a boundary that cannot carry a string cheaply — the
   * contact tap fires inside the solver's inner loop, and the C# core has to produce the same
   * number. Held on the material rather than looked up per contact, because the lookup would
   * be a hash access a thousand times a second to recover something the object already knows.
   */
  readonly index: number
  /** Restitution at zero impact speed. */
  readonly restitution: number
  /**
   * Reduction in bounciness with impact speed, on Visual Pinball's scale:
   * `0` is no falloff, `1.0` halves restitution at 1 m/s.
   */
  readonly falloff: number
  /** Coulomb friction coefficient. */
  readonly friction: number
  /** Randomised outgoing deflection, radians. Drawn from the replay PRNG, never Math.random. */
  readonly scatter: number
  /** Reference speed for the falloff curve, m/s. */
  readonly falloffVelocity: number
  /** Floor on effective restitution, so a hard hit is dead but not perfectly inelastic. */
  readonly minRestitution: number
}

/**
 * The materials in code order. A name's position here is its wire code.
 *
 * Declared before the table it numbers, so adding a material is one edit rather than two that
 * can disagree. Appending is safe; reordering renumbers every recorded contact.
 */
export const MATERIAL_NAMES: readonly MaterialName[] = [
  'rubber',
  'flipperRubber',
  'metal',
  'wood',
  'plastic',
  'wire',
  'glass',
]

function material(
  name: MaterialName,
  restitution: number,
  falloff: number,
  friction: number,
  scatterDegrees: number,
): Material {
  return {
    name,
    index: MATERIAL_NAMES.indexOf(name),
    restitution,
    falloff,
    friction,
    scatter: toRadians(scatterDegrees),
    falloffVelocity: 1.0,
    minRestitution: 0.1,
  }
}

/**
 * The seven materials.
 *
 * Playfield friction of 0.20 is deliberate. Values near 0.075 are common in amateur
 * tables and are exactly why those tables feel like air hockey; 0.15–0.25 is the range
 * that matches real machines.
 */
export const MATERIALS: Readonly<Record<MaterialName, Material>> = {
  rubber: material('rubber', 0.88, 0.15, 0.4, 1.0),
  flipperRubber: material('flipperRubber', 0.88, 0.15, 0.9, 0.0),
  metal: material('metal', 0.45, 0.05, 0.15, 1.5),
  wood: material('wood', 0.35, 0.05, 0.2, 2.0),
  plastic: material('plastic', 0.55, 0.1, 0.25, 1.0),
  wire: material('wire', 0.3, 0.05, 0.12, 0.5),
  glass: material('glass', 0.3, 0.1, 0.2, 0.0),
}

/**
 * Effective restitution at a given normal impact speed (PH-040).
 *
 * ```
 * e_eff = max(e_min, e · (1 − 0.5 · falloff · |v_n| / v_ref))
 * ```
 *
 * The `0.5` factor is what makes `falloff = 1.0` mean "halved at 1 m/s", matching the
 * documented Visual Pinball semantic. At the standard rubber values this yields 0.867 at
 * 0.2 m/s falling to 0.484 at 6 m/s — lively when nudged, comparatively dead when struck
 * hard, which is what real rubber does.
 */
export function effectiveRestitution(m: Material, normalSpeed: number): number {
  const speed = normalSpeed < 0 ? -normalSpeed : normalSpeed
  const e = m.restitution * (1 - 0.5 * m.falloff * (speed / m.falloffVelocity))
  return e < m.minRestitution ? m.minRestitution : e
}

/** Per-machine overrides, bounded so a definition cannot ask for a frictionless playfield. */
export interface MaterialOverride {
  readonly restitution?: number
  readonly falloff?: number
  readonly friction?: number
  readonly scatterDegrees?: number
}

/**
 * Permitted range of every per-machine override.
 *
 * Exported because the machine-definition validator has to reject exactly what this table
 * would otherwise silently clamp. Two copies of these numbers means an author can write
 * `friction: 1.2`, pass validation, and play a machine with `friction: 1.0` — with nothing
 * anywhere telling them their number was not the machine's number.
 */
export const MATERIAL_OVERRIDE_BOUNDS = {
  restitution: [0.0, 0.95],
  falloff: [0.0, 1.0],
  friction: [0.0, 1.5],
  scatterDegrees: [0.0, 10.0],
} as const

const BOUNDS = MATERIAL_OVERRIDE_BOUNDS

function bound(value: number, [lo, hi]: readonly [number, number]): number {
  return value < lo ? lo : value > hi ? hi : value
}

/** Apply a machine's overrides on top of the defaults, clamped to the published bounds. */
export function applyOverrides(
  overrides: Partial<Record<MaterialName, MaterialOverride>> | undefined,
): Record<MaterialName, Material> {
  const out = { ...MATERIALS } as Record<MaterialName, Material>
  if (!overrides) return out

  for (const key of Object.keys(out) as MaterialName[]) {
    const o = overrides[key]
    if (!o) continue
    const base = out[key]
    out[key] = {
      ...base,
      restitution: o.restitution === undefined ? base.restitution : bound(o.restitution, BOUNDS.restitution),
      falloff: o.falloff === undefined ? base.falloff : bound(o.falloff, BOUNDS.falloff),
      friction: o.friction === undefined ? base.friction : bound(o.friction, BOUNDS.friction),
      scatter:
        o.scatterDegrees === undefined
          ? base.scatter
          : toRadians(bound(o.scatterDegrees, BOUNDS.scatterDegrees)),
    }
  }
  return out
}
